import { Form } from '@ant-design/compatible';
import { Col } from 'antd';
import AlertsContainer from 'common/alerts/containers/alert-container';
import ErrorHandler from 'common/error/components/error-handler';
import LockMessage from 'common/lock/components/lock-message';
import ActionButtons from 'common/navigation/components/action-buttons';
import AdditionalPartners from 'common/partners/components/additional-partners';
import MainPartners from 'common/partners/components/main-partners';
import PublishSteps from 'common/publish-steps/containers/publish-steps-container';
import Spinner from 'common/spinner/components/spinner';
import cloneDeep from 'lodash.clonedeep';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Outlet, Route, Routes } from 'react-router-dom';
import { compose } from 'redux';
import styled from 'styled-components';
import TicketButton from 'tickets/ticket-button';
import { Button, Div, Flex, Icon, Info, Modal, notification, Row, Title, TitleActions } from 'ui';
import {
  mainPartnerTypes,
  orderStatus,
  partnerTypes as partnerTypeByName,
} from 'utils/app-configs';
import { withRouter } from 'utils/withRouter';
import PermissionCheck from '../../../common/features/permission-check';
import { dotAlternative, referenceTypes } from '../../../utils/app-configs';
import { checkErrorObject, isObjectNullOrEmpty } from '../../../utils/helpers';
import Prompt from '../../../utils/prompt';
import ChangePartner from '../../change-partner/containers/change-partner-container';
import FieldEngineer from '../../field-service-engineer/field-engineer';
import Parts from '../../parts/containers/parts-container';
import Planning from '../../planning/planning';
import SpecialHandlingTransport from '../../special-handling-transport/special-handling-transport';
import Subcontracts from '../../subcontracts/containers/subcontract-container';
import SubOrder from '../../suborder/suborder';
import TaskList from '../../task-list/containers/task-list-container';
import General from './general';
import Notes from './notes';
import RequestToPlan from './request-to-plan';
import StatusBar from './status-bar';

const Content = styled.div`
  margin-bottom: 45px;
`;

const DetailsTitle = styled(Title)`
  padding-bottom: 0;
  flex-basis: 100%;
`;

const OrdersSubTitle = styled(Title)`
  font-size: 15px;
  font-weight: bold;
  padding-bottom: 0px;
  padding-top: 5px;
  text-transform: uppercase;
`;

const OrdersSubTitleWrapper = styled.div`
  padding-bottom: 20px;
  padding-top: 15px;
`;

const OrderIcon = styled(Icon)`
  color: ${p => p.orderType === 'main' ? '#ff9600' : '#0078a1'};
  margin-right: ${p => p.small ? '5px' : '0'};
`;

const OrderLink = styled.a`
  color: #0078a1;
  text-decoration: underline;
  &:hover {
    color: #0098cc;
    text-decoration: underline;
  }
`;

const StyledUl = styled.ul`
  list-style: disc;
  margin-left: 20px;
`;

// Is necessary because antd doesn't allow type'secondary' in a notification
const StyledConfirmButton = styled(Button)`
  text-transform: uppercase;
  border-color: #6f7e95 !important;
  background-color: #6f7e95 !important;
  color: white !important;
  &:hover {
    border-color: #6f7e95 !important;
    background-color: #6f7e95 !important;
    color: white !important;
    opacity: 0.7;
  }
`;

const CreationInfoText = styled.div`
  color: rgba(0, 0, 0, 0.88);
`;

const StyledTitleActions = styled(TitleActions)`
  margin: 0;
  flex-basis: 50%;
`;

const StyledLockMessage = styled(LockMessage)`
  flex-basis: 100% !important;
`;

const ListItem = styled.p`
  margin-bottom: 15px;
`;

const StyledFlex = styled(Flex)`
  ${p => p.shouldShowSmartActionModal === true && 'flex-wrap: wrap;'}
`;

const StyledIcon = styled(Icon)`
  color: #0078a1;
  margin-right: ${p => p.small ? '5px' : '0'};
`;

const Overlay = styled.div`
  height: auto;
  min-height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: #6f7e95;
  z-index: 130;
  opacity: 0.5;
`;

const StyledButton = styled(Button)`
  margin-top: 20px;
  text-transform: uppercase;
  cursor: pointer;
  width: 100px;
  float: right;
  color: white;
  height: 32px;
  background-color: #6f7e95 !important;
  &:hover {
    opacity: 0.7;
    color: white;
    background-color: #6f7e95 !important;
  }
  &:focus {
    color: white;
    background-color: #6f7e95 !important;
  }
`;

class OrderDetails extends Component {
  static propTypes = {
    getOrderDetailsRequest: PropTypes.func.isRequired,
    saveOrderRequest: PropTypes.func.isRequired,
    deleteDraftRequest: PropTypes.func.isRequired,
    clearNavigationalInfo: PropTypes.func.isRequired,
    savePartnerRequest: PropTypes.func,
    id: PropTypes.string.isRequired,
    data: PropTypes.object,
    form: PropTypes.object,
    isLoading: PropTypes.bool,
    isSaving: PropTypes.bool,
    saveSuccess: PropTypes.bool,
    removeSuccess: PropTypes.bool,
    intl: PropTypes.object,
    error: PropTypes.object,
    notes: PropTypes.array,
    changes: PropTypes.object,
    serviceNoteValue: PropTypes.string,
    invoiceNoteValue: PropTypes.string,
    lock: PropTypes.object,
    partners: PropTypes.array,
    renderMenu: PropTypes.func.isRequired,
    partnerTypes: PropTypes.array,
    orderPartList: PropTypes.object,
    subcontracts: PropTypes.array,
    linkedOrders: PropTypes.array,
    history: PropTypes.object,
    orderStates: PropTypes.array,
    showOrderEdit: PropTypes.bool,
    progressInfo: PropTypes.func,
    hasMultipleOrders: PropTypes.bool,
    addSubOrderRequest: PropTypes.func,
    redirect: PropTypes.object,
    isDeletingDraft: PropTypes.bool,
    getOrderDetailsSuccess: PropTypes.func,
    linkedPlanning: PropTypes.array,
    openPlanningAndView: PropTypes.func,
    warnings: PropTypes.array,
    getLinkedPlanningsRequest: PropTypes.func,
    getReferenceData: PropTypes.func,
    resetDirtyIndicator: PropTypes.bool,
    validationInfo: PropTypes.array,
    operationInfo: PropTypes.object,
    userStatuses: PropTypes.object,
    getOrderDetailsStatusRequest: PropTypes.func,
    serviceCenterId: PropTypes.string,
    isLoadingTasklist: PropTypes.bool,
    useSockets: PropTypes.bool,
    resetFields: PropTypes.bool,
    isExternalRemove: PropTypes.bool,
    externalRemove: PropTypes.func,
    userId: PropTypes.string,
  };

  state = {
    isFormDirty: false,
    isRemoveModalVisible: false,
    isSidePanelOpen: false,
    preventRender: false,
    isSummaryOpen: true,
    showOverlay: false,
  };

  componentWillMount() {
    const { id, getOrderDetailsRequest, data } = this.props;

    // check to make sure everything initial render is not executed
    // when having old data in our props.
    // otherwise wrong initialValues will be filled in!!
    if (id && data && data.id && data.id !== id) {
      this.setState({ preventRender: true });
    }

    getOrderDetailsRequest({ id, shouldRefetchPartners: true });
  }

  // TODO fix complexity with refactor of details
  // eslint-disable-next-line complexity
  componentWillReceiveProps(nextProps) {
    // TODO: check if this is not an anti-pattern
    if (
      (nextProps.id && nextProps.data && nextProps.id === nextProps.data.id) ||
      nextProps?.error?.status === 410
    ) {
      this.setState({ preventRender: false });
    }
    if (
      this.props.saveSuccess !== nextProps.saveSuccess &&
      !nextProps.isSaving &&
      !nextProps.warnings?.length
    ) {
      nextProps.form.resetFields();
      this.setState({ isFormDirty: false });
    }
    if (!this.props.resetFields && nextProps.resetFields) {
      if ((this.state.isFormDirty || this.props.form.isFieldsTouched()) &&
        nextProps.lock?.lockedById !== nextProps.userId) {
        notification.warning({
          key: 'external-update',
          message: nextProps.intl.formatMessage({ id: 'type.order.EXTERNAL_UPDATE' }),
          placement: 'top',
        });
      }
      nextProps.form.resetFields();
      this.setState({ preventRender: false, isFormDirty: false });
    }

    if (
      this.props.resetDirtyIndicator !== nextProps.resetDirtyIndicator &&
      nextProps.resetDirtyIndicator
    ) {
      this.setState({ isFormDirty: false });
    }

    if (!this.props.warnings?.length && nextProps.warnings?.length) {
      notification.warning({
        key: 'order-warning',
        message: nextProps.intl.formatMessage({ id: 'lock.WARNING' }),
        description: nextProps.warnings?.map((w, i) => <ListItem key={i}>{w}</ListItem>),
        className: 'extended-notification',
        duration: 0,
        placement: 'top',
      });
    }

    if (nextProps.id !== this.props.id) {
      this.resetForm(nextProps.id);
    }

    if (
      (this.props.changes?.operationsAddedFromSmartAction !==
        nextProps?.changes?.operationsAddedFromSmartAction
        ||
        this.props.changes?.partsAddedFromSmartAction !==
        nextProps?.changes?.operationsAddedFromSmartAction
      )
      &&
      (nextProps.changes?.operationsAddedFromSmartAction ||
        nextProps.changes?.partsAddedFromSmartAction)
    ) {
      this.setState({ isFormDirty: true });
    }
    if (
      !isObjectNullOrEmpty(nextProps.redirect) &&
      nextProps.redirect.url !== this.props.redirect?.url
    ) {
      notification.success({
        message: this.props.intl.formatMessage({ id: nextProps.redirect.key }),
        placement: 'top',
      });
      this.handleOpenMultipleSubOrders(nextProps.redirect.url);
    }
    if (nextProps.id !== this.props.id) {
      // do new request for data
      const { id, getOrderDetailsRequest, data } = nextProps;
      getOrderDetailsRequest({ id, shouldRefetchPartners: data?.showIncoTerm });
    }
    if (nextProps.error !== this.props.error) {
      // Close the modal when an error occured
      this.setState({ isRemoveModalVisible: false });
    }
    if (!this.props.removeSuccess && nextProps.removeSuccess) {
      nextProps.form.resetFields();
      this.setState({ isFormDirty: false, isRemoveModalVisible: false });
      notification.success({
        key: 'draft-delete',
        message: nextProps.intl.formatMessage({ id: 'type.order.DRAFT_DELETE_SUCCESS' }),
        placement: 'top',
      });
      nextProps.clearNavigationalInfo();
      this.navigateBack(nextProps);
    }
    if (
      !this.props.isExternalRemove &&
      nextProps.isExternalRemove &&
      !nextProps.removeSuccess &&
      !this.props.removeSuccess
    ) {
      this.setState({ showOverlay: true });
      notification.warning({
        key: 'external-remove',
        placement: 'top',
        message: <>
          {nextProps.intl.formatMessage({ id: 'type.order.DRAFT_EXTERNAL_DELETE_SUCCESS' })}
          <StyledButton
            onClick={() => {
              notification.destroy('external-remove');
              nextProps.form.resetFields();
              this.setState({
                isFormDirty: false,
                isRemoveModalVisible: false,
                showOverlay: false,
              });
              nextProps.clearNavigationalInfo();
              this.navigateBack(nextProps, true);
              nextProps.externalRemove(false);
            }}
          >
            {nextProps.intl.formatMessage({ id: 'common.CLOSE' })}
          </StyledButton>
        </>,
        duration: 0,
        closeIcon: null,
      });
    }
  }

  onRemoveModalCancel = () => {
    this.setState({ isRemoveModalVisible: false });
  };

  onRemoveOrder = () => {
    const { deleteDraftRequest, id } = this.props;
    deleteDraftRequest({ id });
  };

  setFormDirty = () => {
    if (!this.state.isFormDirty) {
      this.setState({ isFormDirty: true });
    }
  };

  setSidepanelClosed = () => {
    this.setState({ isSidePanelOpen: false });
  };

  setSidepanelOpened = () => {
    this.setState({ isSidePanelOpen: true });
  };

  getErrorList = (errors, errorList) => {
    if (errors) {
      Object.values(errors).forEach((value) => {
        if (value && value.errors) {
          value.errors.forEach((v) => {
            errorList.push(v.message);
          });
        } else {
          this.getErrorList(value, errorList);
        }
      });
    }
  };

  navigateBack = (nextProps, external = false) => {
    const { hasMultipleOrders } = this.props;
    if (!hasMultipleOrders) {
      const parentEquipment = nextProps?.activeParents?.find(e =>
        e?.type === referenceTypes.equipment,
      );
      const parentFunctionalLocation = nextProps?.activeParents?.find(e =>
        e?.type === referenceTypes.functionallocation,
      );
      if (nextProps.history?.canGoBack && !nextProps.isBackNavigationBlocked && !external) {
        nextProps.history.back();
      } else if (parentEquipment) {
        nextProps.history
          .replace(`/equipment/${parentEquipment.id}?context=${parentEquipment.id}`);
      } else if (parentFunctionalLocation) {
        nextProps.history
          // eslint-disable-next-line max-len
          .replace(`/functionallocation/${parentFunctionalLocation.id}?context=${parentFunctionalLocation.id}`);
      } else {
        nextProps.history.push('/');
      }
    }
  };

  openSupersededParts = (list) => {
    this.setState({ hasOpenSupersededParts: list });
  };

  fetchPartners = (incoTermValue) => {
    const { data, getReferenceData, serviceCenterId } = this.props;
    getReferenceData({
      objectType: 'order',
      name: 'partnerTypes',
      urlParams: { orderId: data.id, incoTerm: incoTermValue },
      serviceCenterId,
    });
  };

  save = (status) => {
    const { saveOrderRequest, form, data, intl } = this.props;
    const key = 'validationErrorWarning';

    const btn =
      <StyledConfirmButton onClick={() => notification.destroy(key)}>
        {intl.formatMessage({ id: 'common.OK' })}
      </StyledConfirmButton>;
    form.validateFields((err, values) => {
      if (err || this.state.hasOpenSupersededParts) {
        notification.error({
          message: intl.formatMessage({ id: 'statusBar.VALIDATION_ERROR' }),
          placement: 'top',
          className: 'extended-notification',
          description: this.renderValidationErrorList(err),
          duration: 0,
          btn,
          key,
        });
      } else {
        // merge the editParts with the parts
        const mergedValues = cloneDeep(values);
        if (mergedValues?.parts && Object.keys(mergedValues.parts)?.length) {
          const parts = Object.entries(mergedValues?.parts).reduce((tot, [k, v]) => ({
            ...tot,
            [k.replace(dotAlternative, '.')]: v,
          }), {});

          const orderPartList = mergedValues.orderPartList.parts
            ?.map((p) => {
              const part = parts[p.id];
              return {
                ...p,
                ...part,
              };
            });
          mergedValues.parts = parts;
          mergedValues.orderPartList.parts = orderPartList;
        }
        saveOrderRequest({ data, values: mergedValues, status });
      }
    });
  };

  showRemoveModal = () => {
    this.setState({ isRemoveModalVisible: true });
  };

  toggleIsSummaryOpen = () => {
    this.setState({
      isSummaryOpen: !this.state.isSummaryOpen,
    });
  };

  changePartner = (partner) => {
    const { type, name, id, isNew } = partner;
    this.setState({ isSidePanelOpen: true }, () =>
      setTimeout(() => this.props.history.push(`change-partner/${type}/${name}/${id}/${isNew}`)),
    );
  };

  removePartner = ({ id }) => {
    const { savePartnerRequest, data } = this.props;
    savePartnerRequest({ order: data, removePartnerId: id });
  };

  resetForm = (manualId) => {
    const { form, getOrderDetailsRequest, id } = this.props;
    form.resetFields();
    this.setState({ isFormDirty: false });
    if (manualId) {
      getOrderDetailsRequest({ id: manualId });
    } else {
      getOrderDetailsRequest({ id });
    }
  };

  handleAddSubOrder = (technicianNumber, technicianName) => {
    const {
      addSubOrderRequest,
      data: { id: orderId },
    } = this.props;
    addSubOrderRequest({ technicianNumber, technicianName, orderId });
  };

  /**
   * When we add a suborder, we always open a multibar,
   * and we go directly to the second order. (First is main order)
   */
  handleOpenMultipleSubOrders = (redirectUrl) => {
    const { history } = this.props;
    const url = redirectUrl.split('/order');
    const [, context] = url && url[1].split('/');
    history.push(`/${redirectUrl}/position/1`, { context });
  };

  handleOpenSubOrder = (orderId) => {
    const { history } = this.props;
    history.push(`/order/${orderId}`, { context: orderId });
  };

  handleOpenTicket = (data) => {
    const { history } = this.props;
    if (data?.suggestedTicketId && data.suggestedTicketNumber) {
      history.push(`/ticket/${data.suggestedTicketNumber}/${data.suggestedTicketId}`);
    }
  };

  handleOpenLinksClick = (e) => {
    e.preventDefault();
    // Add current order and go to second order in the sequence
    const { history, linkedOrders, id } = this.props;
    const orders = linkedOrders && linkedOrders?.map(o => encodeURIComponent(o.id));
    // Redirect to second order in sequence and update the context
    history.push(`/order/${id},${orders.join(',')}/position/1`, { context: orders[0] });
  };

  // Check if the multiple orders are all linked orders.
  // If so, don't show "Open linked orders" text.
  // If not, there's an order that is not linked,
  // so we show the "open linked orders" text if needed.
  checkMultipleOrdersAreSuborders = (linkedOrders) => {
    let isSuborderArray = true;
    if (linkedOrders.length) {
      linkedOrders.forEach((lo) => {
        // While it's a suborder array keep checking.
        // As soon as one id is not linked, set to false and stop checking.
        if (isSuborderArray) {
          // Get all the ids from the path and decode
          isSuborderArray = decodeURIComponent(
            location.pathname.split('/')[2].split(','),
          ).includes(lo.id);
        }
      });
    }
    return isSuborderArray;
  };

  renderValidationErrorList = (errors) => {
    const { hasOpenSupersededParts } = this.state;
    const { intl } = this.props;
    const errorList = [];
    this.getErrorList(errors, errorList);
    return (
      <StyledUl>
        {errorList && errorList?.map((error, index) => <li key={index}>{error}</li>)}
        {hasOpenSupersededParts &&
          <li key={errorList.length || 0}>
            {intl.formatMessage({ id: 'order.parts.supersededParts.ACTION_REQUIRED' })}
          </li>}
      </StyledUl>
    );
  };

  renderSubOrderSubTitle = (linkedOrders, data) => {
    const { intl } = this.props;
    if (linkedOrders.length !== 0) {
      let icon;
      if (data.isSubOrder) {
        icon = <OrderIcon small={true} type='link' />;
      } else {
        icon = <OrderIcon small={true} orderType='main' type='crown' />;
      }
      return (
        <>
          {icon}
          <FormattedMessage
            id='order.fieldEngineer.DETAILS_SUBTITLE'
            values={{
              orderType: data.isSubOrder
                ? intl.formatMessage({ id: 'order.fieldEngineer.SUB' })
                : intl.formatMessage({ id: 'order.fieldEngineer.MAIN' }),
              ordersLength: linkedOrders.length,
              wordOrder:
                linkedOrders.length !== 1
                  ? intl.formatMessage({ id: 'order.fieldEngineer.ORDERS' })
                  : intl.formatMessage({ id: 'order.fieldEngineer.ORDER' }),
              link:
                linkedOrders.length > 0 &&
                !this.checkMultipleOrdersAreSuborders(linkedOrders) &&
                <OrderLink onClick={this.handleOpenLinksClick}>
                  <FormattedMessage
                    id='order.fieldEngineer.OPEN_LINKED_ORDERS'
                    values={{
                      wordOrder:
                        linkedOrders.length !== 1
                          ? intl.formatMessage({ id: 'order.fieldEngineer.ORDERS' })
                          : intl.formatMessage({ id: 'order.fieldEngineer.ORDER' }),
                    }}
                  />
                </OrderLink>
              ,
            }}
          />
        </>
      );
    }
    return null;
  };

  renderSmartActionSubTitle = (data) => {
    if (data?.smartActionLinkingInfo?.isOrderLinkedToAction &&
      data.smartActionLinkingInfo.suggestedTicketId &&
      data.smartActionLinkingInfo.suggestedTicketNumber) {
      return (
        <>
          <StyledIcon small={true} type='wand-magic-sparkles' />
          <FormattedMessage
            id='order.HAS_SMART_ACTION'
            values={{
              link: <OrderLink
                onClick={() =>
                  this.handleOpenTicket(data?.smartActionLinkingInfo)}
              >
                <FormattedMessage id='order.HAS_SMART_ACTION_LINK' />
              </OrderLink>,
            }}
          />
        </>
      );
    }
    return null;
  };

  renderTitles = (shouldShowSmartActionModal) => {
    const { data, lock, intl, linkedOrders } = this.props;
    return (
      <>
        <StyledFlex shouldShowSmartActionModal={shouldShowSmartActionModal}>
          <DetailsTitle data-test-id='order-description'>{data.name}</DetailsTitle>
          <StyledTitleActions>
            <StyledLockMessage lock={lock} />
          </StyledTitleActions>
          {data.createdByName && data.createdOn &&
            <StyledTitleActions>
              <CreationInfoText>
                {intl.formatMessage(
                  { id: 'order.CREATION_INFO' },
                  {
                    name: data.createdByName,
                    date: intl.formatDate(data.createdOn),
                    time: intl.formatTime(data.createdOn),
                  },
                )}
              </CreationInfoText>
            </StyledTitleActions>
          }
        </StyledFlex>
        <OrdersSubTitleWrapper>
          <Flex>
            <OrdersSubTitle>
              {this.renderSubOrderSubTitle(linkedOrders, data)}
            </OrdersSubTitle>
          </Flex>
          <Flex>
            <OrdersSubTitle>
              {this.renderSmartActionSubTitle(data)}
            </OrdersSubTitle>
          </Flex>
        </OrdersSubTitleWrapper>
      </>
    );
  };

  // eslint-disable-next-line
  render() {
    const {
      data,
      form,
      isSaving,
      isLoading,
      error,
      renderMenu,
      notes,
      serviceNoteValue,
      invoiceNoteValue,
      partners,
      partnerTypes,
      orderPartList,
      subcontracts,
      linkedOrders,
      linkedPlanning,
      orderStates,
      lock,
      showOrderEdit,
      intl,
      progressInfo,
      hasMultipleOrders,
      history,
      isDeletingDraft,
      id,
      getOrderDetailsRequest,
      getOrderDetailsSuccess,
      openPlanningAndView,
      getLinkedPlanningsRequest,
      validationInfo,
      operationInfo,
      userStatuses,
      getOrderDetailsStatusRequest,
      isLoadingTasklist,
      isExternalRemove,
      useSockets,
    } = this.props;

    const { preventRender, isSidePanelOpen } = this.state;
    const dirty = form.isFieldsTouched() || this.state.isFormDirty;
    const disabled = isSaving ||
      isLoading ||
      !!lock ||
      !showOrderEdit ||
      isLoadingTasklist ||
      isExternalRemove ||
      data.isReadOnly ||
      (data.statusInfo && data.statusInfo.id === orderStatus.unknown) ||
      (data.statusInfo?.id >= orderStatus.adminClosed);

    const mainPartnersArray = mainPartnerTypes.reduce((a, i) => {
      a.push(i.type.toUpperCase());
      return a;
    }, []);

    const mainPartners = partners.reduce((all, p) => {
      if (mainPartnersArray.includes(p.partnerFunction.toUpperCase())) {
        // Filter out double ids
        if (!all?.find(par => par.id === p.id)) {
          return [...all, p];
        }
      }

      return all;
    }, []);

    const additionalPartners = partners?.filter(
      p => !mainPartnersArray.includes(p.partnerFunction.toUpperCase()),
    );

    const mainTypes =
      partnerTypes && partnerTypes?.filter(p => mainPartnersArray.includes(p.type.toUpperCase()));
    /* eslint-enable max-len */

    const additionalTypes =
      partnerTypes && partnerTypes?.filter(p => !mainPartnersArray.includes(p.type));

    const shipTo = partners?.find(
      p => p.partnerFunction && p.partnerFunction.toUpperCase() === partnerTypeByName.SHIPTO,
    );

    const shouldShowSubcoWarning =
      Object.keys(data).length > 0 && ['DRFT', 'CRTD'].includes(data.statusInfo.name);

    if (preventRender) {
      return <Spinner title={intl.formatMessage({ id: 'common.LOADING_DETAILS' })} />;
    }

    const errors = form?.getFieldsError();
    const hasError = !isObjectNullOrEmpty(errors) && !!checkErrorObject(errors)?.length;
    const shouldShowSmartActionModal = !disabled &&
      data.statusInfo?.id < orderStatus.executed &&
      !data?.smartActionLinkingInfo?.isOrderLinkedToAction &&
      data?.smartActionLinkingInfo?.availableSmartActionsCount > 0;

    return (
      <>
        {this.state.showOverlay ? <Overlay /> : null}
        {renderMenu(
          <ActionButtons>
            {data.isEPadEnabled &&
              <Button type='secondary' link={data.ePadUrl}>
                {intl.formatMessage({ id: 'order.openEpad' }).toUpperCase()}
                <Icon theme='light' type='external-link-alt' marginLeft={10} />
              </Button>
            }
            <TicketButton id={data.id} />
          </ActionButtons>,
        )}
        <ErrorHandler type='notification' error={error} />
        <PublishSteps
          id={id}
          reloadData={() => getOrderDetailsRequest({ id })}
          onDataReloaded={getOrderDetailsSuccess}
          type='order'
          hasPlanningItems={linkedPlanning?.length > 0}
          validationInfo={validationInfo}
          isExternalCreate={data?.isExternalCreate}
          isExternalUpdate={data?.isExternalUpdate}
          isGtsBlocked={data?.isGtsBlocked}
          useSockets={useSockets}
        />
        {isLoading ?
          <Spinner title={intl.formatMessage({ id: 'common.LOADING_DETAILS' })} />
          :
          <>
            <Info>
              <Form layout='vertical'>
                <Content>
                  <Row type='flex' gutter={20}>
                    <Col sm={shouldShowSmartActionModal ? 12 : 24} xs={24}>
                      {this.renderTitles(shouldShowSmartActionModal)}
                    </Col>
                    {shouldShowSmartActionModal &&
                      <Col sm={12} xs={24}>
                        <RequestToPlan
                          orderId={id}
                          smartActionLinkingInfo={data?.smartActionLinkingInfo}
                        />
                      </Col>
                    }
                  </Row>
                  <Row type='flex' gutter={20} marginBottom={45}>
                    <Col sm={12} xs={24}>
                      <MainPartners
                        cardsPerLine={2}
                        partners={mainPartners}
                        types={mainTypes}
                        canEdit={!disabled}
                        changePartner={this.changePartner}
                        isLoading={isLoading}
                        activeStatus={data.statusInfo}
                        isPTS={data.isPTS}
                      />
                    </Col>
                    <Col sm={12} xs={24}>
                      <Div marginBottom={20}>
                        <AlertsContainer maxVisibleAlerts={3} small={true} />
                      </Div>
                      <Notes
                        small={true}
                        notes={notes}
                        serviceNoteValue={serviceNoteValue}
                        invoiceNoteValue={invoiceNoteValue}
                        form={form}
                        isSaving={isSaving}
                        disabled={disabled}
                      />
                    </Col>
                  </Row>
                  <Row marginBottom={45} marginRight={20}>
                    <AdditionalPartners
                      orderType={data.businessIndicator}
                      partners={additionalPartners}
                      types={additionalTypes}
                      changePartner={this.changePartner}
                      removePartner={this.removePartner}
                      isLoading={isLoading}
                      activeStatus={data.statusInfo}
                      disabled={disabled}
                    />
                  </Row>
                  <Row type='flex' gutter={20}>
                    <Col span={24}>
                      <General
                        form={form}
                        isSaving={isSaving}
                        isDisabled={disabled}
                        isLoading={isLoading}
                        setFormDirty={this.setFormDirty}
                        data={data}
                        fetchPartners={this.fetchPartners}
                        userStatuses={userStatuses}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      {linkedPlanning?.length ?
                        <Planning
                          plannings={linkedPlanning}
                          openPlanningAndView={openPlanningAndView}
                          getLinkedPlanningsRequest={getLinkedPlanningsRequest}
                          cbPlanningChange={
                            () => getOrderDetailsStatusRequest({
                              id,
                              shouldRefetchPartners: false,
                              updateOrderDetailsStatus: true,
                            })
                          }
                          entityId={data.id}
                          useSockets={useSockets}
                        /> :
                        <FieldEngineer
                          dirty={dirty}
                          hasError={Object.keys(error).length > 0}
                          engineer={
                            data.technicianNumber && {
                              number: data.technicianNumber,
                              name: data.technicianName,
                            }
                          }
                          preferredEngineer={
                            data.prefWorkCenterNumber && {
                              number: data.prefWorkCenterNumber,
                              name: data.prefWorkCenterName,
                            }
                          }
                          lastVisitEngineer={
                            data.lastVisitTechnicianNumber && {
                              number: data.lastVisitTechnicianNumber,
                              name: data.lastVisitTechnicianName,
                            }
                          }
                          recommendedEngineers={data.recommendedEngineers}
                          subOrders={linkedOrders}
                          planboardUrl={data.movilizerUrl}
                          activeStatus={data.statusInfo}
                          onAddSubOrder={this.handleAddSubOrder}
                          onOpenSubOrder={this.handleOpenSubOrder}
                          form={form}
                          disabled={disabled}
                          cannotMovePastMainOrder={data.cannotMovePastMainOrder}
                          isSubOrder={data.isSubOrder}
                          isLocked={!!lock}
                        />
                      }
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <SubOrder
                        dirty={dirty}
                        hasError={Object.keys(error).length > 0}
                        engineer={
                          data.technicianNumber && {
                            number: data.technicianNumber,
                            name: data.technicianName,
                          }
                        }
                        preferredEngineer={
                          data.prefWorkCenterNumber && {
                            number: data.prefWorkCenterNumber,
                            name: data.prefWorkCenterName,
                          }
                        }
                        lastVisitEngineer={
                          data.lastVisitTechnicianNumber && {
                            number: data.lastVisitTechnicianNumber,
                            name: data.lastVisitTechnicianName,
                          }
                        }
                        subOrders={linkedOrders}
                        activeStatus={data.statusInfo}
                        onAddSubOrder={this.handleAddSubOrder}
                        onOpenSubOrder={this.handleOpenSubOrder}
                        form={form}
                        disabled={disabled}
                        cannotMovePastMainOrder={data.cannotMovePastMainOrder}
                        isSubOrder={data.isSubOrder}
                        isLocked={!!lock}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <Parts
                        actualCost={data.actualCost}
                        data={orderPartList}
                        form={form}
                        disabled={disabled}
                        orderId={data.id}
                        history={history}
                        setFormDirty={this.setFormDirty}
                        onSidepanelOpen={this.setSidepanelOpened}
                        onSidepanelClosed={this.setSidepanelClosed}
                        hasOpenSupersededParts={this.openSupersededParts}
                        shipTo={shipTo}
                        activeStatus={data.statusInfo}
                      />
                    </Col>
                  </Row>
                  {data?.specialTransport &&
                    Object.keys(data?.specialTransport)?.length &&
                    data?.showSpecialTransport ?
                    // eslint-disable-next-line
                    <Row>
                      <Col span={24}>
                        <SpecialHandlingTransport
                          form={form}
                          setFormDirty={this.setFormDirty}
                          data={data?.specialTransport}
                          disabled={
                            data.statusInfo?.id > orderStatus.created
                          }
                        />
                      </Col>
                    </Row> :
                    null
                  }
                  <Row>
                    <Col span={24}>
                      <Subcontracts
                        subcontracts={subcontracts}
                        activeStatus={data.statusInfo}
                        form={form}
                        disabled={disabled}
                        shouldShowSubcoWarning={shouldShowSubcoWarning}
                        shipTo={shipTo}
                        setFormDirty={this.setFormDirty}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <TaskList
                        visitType={data.visitType}
                        isDraft={data.isDraft}
                        fromScratch={data.fromScratch}
                        setFormDirty={this.setFormDirty}
                        form={form}
                        disabled={disabled}
                        orderId={data.id}
                        activeStatus={data.statusInfo}
                      />
                    </Col>
                  </Row>
                </Content>
                <PermissionCheck permissionsList={['installedbaseread']}>
                  <StatusBar
                    isSaving={isSaving}
                    isDirty={dirty}
                    isLocked={!!lock}
                    hasError={Object.keys(error).length > 0}
                    hasMissingInfo={hasError}
                    activeStatus={data.statusInfo}
                    disabled={disabled}
                    description={data.name}
                    id={data.number}
                    installedAt={data.installedAtName}
                    plannedDate={data.scheduledDate}
                    orderStates={orderStates}
                    save={this.save}
                    resetForm={this.resetForm}
                    removeOrder={this.showRemoveModal}
                    progressInfo={progressInfo}
                    hasMultipleOrders={hasMultipleOrders}
                    operationInfo={operationInfo}
                  />
                </PermissionCheck>
              </Form>
              {/* don't show prompt when navigating to sidepanel or when order is deleted */}
              <Prompt
                when={dirty && !isSidePanelOpen}
                message={intl.formatMessage({ id: 'prompt.UNSAVED_CHANGES' })}
              />

              <Modal
                title={intl.formatMessage({ id: 'type.order.REMOVE_ORDER' })}
                open={this.state.isRemoveModalVisible}
                footer={null}
                onCancel={this.onRemoveModalCancel}
              >
                <Row>
                  <FormattedMessage id='prompt.DELETE_DRAFT_CONFIRMATION' />
                </Row>
                <Flex jcsb margin={10}>
                  <Button type='secondary' onClick={this.onRemoveOrder} loading={isDeletingDraft}>
                    <FormattedMessage id='prompt.DELETE' />
                  </Button>
                  <Button
                    light
                    type='secondary'
                    onClick={this.onRemoveModalCancel}
                    disabled={isDeletingDraft}
                  >
                    <FormattedMessage id='prompt.CANCEL' />
                  </Button>
                </Flex>
              </Modal>
            </Info>
            <Routes>
              <Route
                path={'change-partner/:type/:name/:id/:isNew'}
                element={<PermissionCheck permissionsList={['installedbaseread']}>
                  <ChangePartner
                    isDetailLoading={isLoading}
                    order={data}
                    objectType='order'
                    setFormDirty={this.setFormDirty}
                    setSidepanelClosed={this.setSidepanelClosed}
                    engineer={data.technicianName}
                    preferredEngineer={data.prefWorkCenterName}
                    lastVisitEngineer={data.lastVisitTechnicianName}
                  />
                </PermissionCheck>
                }
              />
            </Routes>
            <Outlet />
          </>
        }
      </>
    );
  }
}

export default compose(
  withRouter,
  Form.create(),
  injectIntl,
)(OrderDetails);
